import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getTithesByYear,
  getActivePositionsForYear,
  getDistrictsForTithes,
  getRegionsForTithes,
} from '../data/tithesRepository';
import type { DistrictEntry, RegionEntry } from '../data/tithesRepository';
import type { Tithe, TitheFilters, TitheSubjectType } from '../types';
import { lastDayOfMonth, lastDayOfMonthISO } from '../types';
import { useIssuedReceiptSourceIds } from '@/features/receipts/hooks/useReceipts';
import { ROLE_LABELS, DISTRICT_ROLES, NATIONAL_ROLES } from '@/models/authorityPosition';
import type { AuthorityRole } from '@/models/authorityPosition';
import { INSTITUTIONS } from '@/features/institutions/config/institutions';
import type { InstitutionConfig } from '@/features/institutions/types';

// ---------------------------------------------------------------------------
// Matrix shape
// ---------------------------------------------------------------------------

export interface MatrixCell {
  tithe: Tithe | null;
  subjectId: string;
  subjectType: TitheSubjectType;
  /** True only for position rows where the month is outside the authority's tenure */
  outOfTenure: boolean;
  late: boolean;
  /** An issued receipt exists for this cell's tithe */
  hasReceipt: boolean;
}

export interface MatrixRow {
  subjectId: string;
  subjectType: TitheSubjectType;
  /** Minister full name (position) or org name (district/region/institution) */
  label: string;
  /** Minister adb_code (position) or empty string */
  sublabel: string;
  /** Role label (position) or org code (district/region) or empty (institution) */
  roleLabel: string;
  /** Position rows only: the minister behind the position, for prefilling receipts */
  ministerId?: string;
  ministerDni?: string | null;
  cells: MatrixCell[]; // index 0=Jan … 11=Dec
  totalPaid: number;
}

export interface MatrixGroup {
  orgType: 'DISTRICT' | 'DISTRICT_ORG' | 'REGION' | 'NATIONAL' | 'INSTITUTION';
  orgId: string;
  orgName: string;
  orgCode: string;
  rows: MatrixRow[];
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useTithes(filters: TitheFilters) {
  const [tithes, setTithes]           = useState<Tithe[]>([]);
  const [positions, setPositions]     = useState<ActivePosition[]>([]);
  const [districts, setDistricts]     = useState<DistrictEntry[]>([]);
  const [regions, setRegions]         = useState<RegionEntry[]>([]);
  const [institutions, setInstitutions] = useState<InstitutionConfig[]>([]);
  const [isLoading, setIsLoading]     = useState(true);
  const [error, setError]             = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const needsPositions    = filters.scope === 'NATIONAL';
      const needsDistrictOrgs = filters.scope === 'DISTRICT';
      const needsRegions      = filters.scope === 'REGION';
      const needsInstitutions = filters.scope === 'INSTITUTION';

      const [titheData, posData, distData, regData] = await Promise.all([
        getTithesByYear(filters),
        needsPositions    ? getActivePositionsForYear(filters.year, filters) : Promise.resolve([]),
        needsDistrictOrgs ? getDistrictsForTithes(filters) : Promise.resolve([]),
        needsRegions      ? getRegionsForTithes(filters) : Promise.resolve([]),
      ]);

      setTithes(titheData);
      setPositions(posData as ActivePosition[]);
      setDistricts(distData as DistrictEntry[]);
      setRegions(regData);
      setInstitutions(needsInstitutions ? INSTITUTIONS : []);
    } catch (e) {
      console.error(e);
      setError('Error al cargar los datos de diezmos.');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const { data: receiptIds } = useIssuedReceiptSourceIds('tithe');
  const matrix = useMemo(
    () => buildMatrix(filters.year, positions, districts, regions, institutions, tithes, receiptIds),
    [filters.year, positions, districts, regions, institutions, tithes, receiptIds],
  );

  return { matrix, isLoading, error, reload: load };
}

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

export interface ActivePosition {
  id: string;
  org_type: 'DISTRICT' | 'REGION' | 'NATIONAL';
  district_id: string | null;
  region_id: string | null;
  role: AuthorityRole;
  started_at: string;
  ended_at: string | null;
  minister: { id: string; adb_code: string; full_name: string | null; dni?: string | null };
  district?: { id: string; name: string | null; code: string | null } | null;
  region?: { id: string; name: string | null; code: string | null; district_id: string } | null;
}

// ---------------------------------------------------------------------------
// Matrix builder
// ---------------------------------------------------------------------------

export function buildMatrix(
  year: number,
  positions: ActivePosition[],
  districtEntries: DistrictEntry[],
  regions: RegionEntry[],
  institutions: InstitutionConfig[],
  tithes: Tithe[],
  receiptIds?: Set<string>,
): MatrixGroup[] {
  // Index tithes by prefixed key for O(1) lookup
  const titheMap = new Map<string, Tithe>();
  for (const t of tithes) {
    if (t.authority_position_id) {
      titheMap.set(`pos:${t.authority_position_id}:${t.month}`, t);
    } else if (t.district_id) {
      titheMap.set(`dis:${t.district_id}:${t.month}`, t);
    } else if (t.region_id) {
      titheMap.set(`reg:${t.region_id}:${t.month}`, t);
    } else if (t.institution_slug) {
      titheMap.set(`ins:${t.institution_slug}:${t.month}`, t);
    }
  }

  const groups: MatrixGroup[] = [];

  // 1. NATIONAL group
  const nationalPositions = positions.filter((p) => p.org_type === 'NATIONAL');
  if (nationalPositions.length > 0) {
    nationalPositions.sort(
      (a, b) => NATIONAL_ROLES.indexOf(a.role) - NATIONAL_ROLES.indexOf(b.role)
    );
    groups.push({
      orgType: 'NATIONAL',
      orgId:   'NATIONAL',
      orgName: 'Directorio Nacional',
      orgCode: '',
      rows: nationalPositions.map((pos) => buildPositionRow(pos, year, titheMap, receiptIds)),
    });
  }

  // 2. DISTRICT position groups (authorities) — shown in Directorio Nacional and Todos tabs
  const districtPositions = positions.filter((p) => p.org_type === 'DISTRICT');
  const districtPosMap = new Map<
    string,
    { meta: Omit<MatrixGroup, 'rows'>; positions: ActivePosition[] }
  >();
  for (const pos of districtPositions) {
    const orgId   = pos.district_id!;
    const orgName = pos.district?.name ?? orgId;
    const orgCode = pos.district?.code ?? '';
    if (!districtPosMap.has(orgId)) {
      districtPosMap.set(orgId, {
        meta: { orgType: 'DISTRICT', orgId, orgName, orgCode },
        positions: [],
      });
    }
    districtPosMap.get(orgId)!.positions.push(pos);
  }
  const districtPosGroups: MatrixGroup[] = [];
  for (const { meta, positions: grpPos } of districtPosMap.values()) {
    grpPos.sort((a, b) => DISTRICT_ROLES.indexOf(a.role) - DISTRICT_ROLES.indexOf(b.role));
    districtPosGroups.push({
      ...meta,
      rows: grpPos.map((pos) => buildPositionRow(pos, year, titheMap, receiptIds)),
    });
  }
  districtPosGroups.sort((a, b) => a.orgCode.localeCompare(b.orgCode));
  groups.push(...districtPosGroups);

  // 3. DISTRICT_ORG group — one row per district (org-level tithes, Distritos tab)
  if (districtEntries.length > 0) {
    groups.push({
      orgType: 'DISTRICT_ORG',
      orgId:   'DISTRICT_ORG',
      orgName: 'Distritos',
      orgCode: '',
      rows: districtEntries.map((dist) => {
        const { cells, totalPaid } = buildOrgCells(dist.id, 'district', year, titheMap, receiptIds);
        return {
          subjectId:   dist.id,
          subjectType: 'district' as const,
          label:       dist.name ?? dist.id,
          sublabel:    '',
          roleLabel:   dist.code ?? '',
          cells,
          totalPaid,
        };
      }),
    });
  }

  // 4. REGION groups — grouped by district, one row per region
  const regionGroupMap = new Map<
    string,
    { orgId: string; orgName: string; orgCode: string; regions: RegionEntry[] }
  >();
  for (const region of regions) {
    const distId   = region.district_id;
    const distName = region.district?.name ?? distId;
    const distCode = region.district?.code ?? '';
    if (!regionGroupMap.has(distId)) {
      regionGroupMap.set(distId, { orgId: distId, orgName: distName, orgCode: distCode, regions: [] });
    }
    regionGroupMap.get(distId)!.regions.push(region);
  }
  const regionGroups: MatrixGroup[] = [];
  for (const { orgId, orgName, orgCode, regions: distRegions } of regionGroupMap.values()) {
    regionGroups.push({
      orgType: 'REGION',
      orgId,
      orgName,
      orgCode,
      rows: distRegions.map((region) => {
        const { cells, totalPaid } = buildOrgCells(region.id, 'region', year, titheMap, receiptIds);
        return {
          subjectId:   region.id,
          subjectType: 'region' as const,
          label:       region.name ?? region.id,
          sublabel:    '',
          roleLabel:   region.code ?? '',
          cells,
          totalPaid,
        };
      }),
    });
  }
  regionGroups.sort((a, b) => a.orgCode.localeCompare(b.orgCode));
  groups.push(...regionGroups);

  // 5. INSTITUTION group — single group, one row per institution
  if (institutions.length > 0) {
    groups.push({
      orgType: 'INSTITUTION',
      orgId:   'INSTITUTION',
      orgName: 'Instituciones y Departamentos',
      orgCode: '',
      rows: institutions.map((inst) => {
        const { cells, totalPaid } = buildOrgCells(inst.slug, 'institution', year, titheMap, receiptIds);
        return {
          subjectId:   inst.slug,
          subjectType: 'institution' as const,
          label:       inst.name,
          sublabel:    '',
          roleLabel:   '',
          cells,
          totalPaid,
        };
      }),
    });
  }

  return groups;
}

// ---------------------------------------------------------------------------
// Row builders
// ---------------------------------------------------------------------------

function buildPositionRow(
  pos: ActivePosition,
  year: number,
  titheMap: Map<string, Tithe>,
  receiptIds?: Set<string>,
): MatrixRow {
  const cells: MatrixCell[] = [];
  let totalPaid = 0;

  for (let m = 1; m <= 12; m++) {
    const monthStart  = new Date(`${year}-${String(m).padStart(2, '0')}-01`);
    const monthEnd    = lastDayOfMonth(year, m);
    const tenureStart = new Date(pos.started_at);
    const tenureEnd   = pos.ended_at ? new Date(pos.ended_at) : new Date('9999-12-31');

    const outOfTenure = monthEnd < tenureStart || monthStart > tenureEnd;
    const tithe = titheMap.get(`pos:${pos.id}:${m}`) ?? null;
    const late  = tithe ? tithe.paid_at.slice(0, 10) > lastDayOfMonthISO(year, m) : false;
    const hasReceipt = !!tithe && !!receiptIds?.has(tithe.id);

    if (tithe) totalPaid += tithe.amount;
    cells.push({ tithe, subjectId: pos.id, subjectType: 'position', outOfTenure, late, hasReceipt });
  }

  return {
    subjectId:   pos.id,
    subjectType: 'position',
    label:       pos.minister.full_name ?? '—',
    sublabel:    pos.minister.adb_code,
    roleLabel:   ROLE_LABELS[pos.role],
    ministerId:  pos.minister.id,
    ministerDni: pos.minister.dni ?? null,
    cells,
    totalPaid,
  };
}

function buildOrgCells(
  subjectId: string,
  subjectType: 'district' | 'region' | 'institution',
  year: number,
  titheMap: Map<string, Tithe>,
  receiptIds?: Set<string>,
): { cells: MatrixCell[]; totalPaid: number } {
  const prefix = subjectType === 'district' ? 'dis' : subjectType === 'region' ? 'reg' : 'ins';
  const cells: MatrixCell[] = [];
  let totalPaid = 0;

  for (let m = 1; m <= 12; m++) {
    const tithe = titheMap.get(`${prefix}:${subjectId}:${m}`) ?? null;
    const late  = tithe ? tithe.paid_at.slice(0, 10) > lastDayOfMonthISO(year, m) : false;
    const hasReceipt = !!tithe && !!receiptIds?.has(tithe.id);
    if (tithe) totalPaid += tithe.amount;
    cells.push({ tithe, subjectId, subjectType, outOfTenure: false, late, hasReceipt });
  }

  return { cells, totalPaid };
}
